import React, { type ComponentProps, useMemo, useRef, useState } from 'react'

import MemberOnboardingBanner from 'components/MemberOnboardingBanner'
import MemberQLEBanner from 'components/MemberQLEBanner'
import MemberRenewalsBanner from 'components/MemberRenewalsBanner'
import { membersContinuationOfCoverage } from 'rails_routes'
import { Banner, ButtonLink, TextLink, Tooltip, useBreakpoint } from 'slab'
import {
  useGetMarketplaceProducts,
  useGetMembersEnrollments,
  useGetMembersInstructions,
  useGetMembersTransactions,
  useGetMembersTransactionsBalanceAndBreakdowns
} from 'utils/web_api'
import {
  type MembersCategorizedEnrollments,
  type MembersEnrollment,
  type MembersInstruction
} from 'utils/web_api.schemas'

import { SkeletonItem } from '../../reusable/SkeletonItem'
import BalanceAndBudgetHeader from '../components/BalanceAndBudgetHeader'
import EnrollmentCard from '../components/EnrollmentCard/EnrollmentCard'
import ExploreMarketplaceCard from '../components/ExploreMarketplaceCard/ExploreMarketplaceCard'
import InstructionsSection from '../components/Instructions/InstructionsSection'
import PlanYearPicker from '../components/PlanYearPicker/PlanYearPicker'
import RecentTransactions from '../components/RecentTransactions/RecentTransactions'
import { UnrecoverableErrorBanner } from '../components/UnrecoverableErrorBanner'
import { useHomePageTracking } from '../hooks/useHomePageTracking'
import { enrollmentDisplayHelper, getGreeting, listToProseString, sortEnrollments } from '../utils'
import ScrollableWithGradients from './components/ScrollableWithGradients'

type MemberGender = 'male' | 'female' | 'non_binary'

interface MemberHomePageProps {
  changePlanPath: string;
  memberGender: MemberGender;
  memberGivenName: string;
  memberOnboardingDate?: Date;
  planYears: number[];
  qmeEnabled: boolean;
  renewalsV2Enabled?: boolean;
  showRenewalsBanner: boolean;
  showOnboardingBanner: boolean;
  activeQLESubtype: null | string;
  qlesV2Enabled?: boolean;
  userIsInactive?: boolean;
  userHasNotSelectedTerminationBenefits?: boolean;
  userIsInCobraEnrollmentWindow?: boolean;
  userIsCobraEligible?: boolean;
  userHasSelectedCobra?: boolean;
  userHasDeclinedTerminationBenefits?: boolean;
  enrollmentWindowEndDate?: string | null;
  hasConfirmedMedicareEnrollment?: boolean;
  /** When set, "change dependents or plans" is disabled; value is the coverage start date for the tooltip. */
  changePlansDisabledAfterSpecialEnrollmentCoverageStartDate?: string | null;
}

const horizontalSeparator = (
  <hr className='w-full border-gray-200 my-6' />
)

const plansTransactionsAndMarketplace = (
  desktopBreakpoint: boolean,
  numberOfCoverageTypes: number,
  planHeaderWithButton: React.ReactElement,
  planCards: React.ReactElement,
  marketplaceSection: React.ReactElement | null,
  recentTransactionsSection: React.ReactElement | null,
  qmeEnabled: boolean
): React.ReactElement => {
  if (desktopBreakpoint) {
    if (numberOfCoverageTypes === 1) {
      // Desktop, one column
      return (
        <>
          <div className='w-full flex flex-row'>
            <section className='flex-1'>
              {planHeaderWithButton}
              <div className='w-full flex flex-col gap-4 flex-1'>
                {planCards}
              </div>
            </section>
            <div className='w-px bg-gray-200 -my-6 mx-6' />
            <div className='flex-1'>
              {qmeEnabled && marketplaceSection}
              {!qmeEnabled && recentTransactionsSection}
            </div>
          </div>
          {horizontalSeparator}
          {qmeEnabled && (
            <div className='w-full'>
              {recentTransactionsSection}
            </div>
          )}
        </>
      )
    }

    // Desktop, multiple columns
    return (
      <>
        <section className='w-full flex flex-col'>
          {planHeaderWithButton}
          <ScrollableWithGradients>
            {planCards}
          </ScrollableWithGradients>
        </section>
        {horizontalSeparator}
        {qmeEnabled && (
          <div className='w-full flex flex-row gap-6'>
            <div className='w-1/2 xl:w-2/3'>
              {recentTransactionsSection}
            </div>
            <div className='w-1/2 xl:w-1/3'>
              {marketplaceSection}
            </div>
          </div>
        )}
        {!qmeEnabled && (
          <div className='w-full'>
            {recentTransactionsSection}
          </div>
        )}
      </>
    )
  }

  // Mobile
  return (
    <>
      <section>
        {planHeaderWithButton}
        <div className='w-full flex flex-col gap-4'>
          {planCards}
        </div>
      </section>
      {horizontalSeparator}
      {recentTransactionsSection}
      {qmeEnabled && (
        <div className='mt-6'>
          {marketplaceSection}
        </div>
      )}
    </>
  )
}

/**
 * Creates a banner component for COBRA-related notifications based on user status.
 *
 * @param userIsInactive - Whether the user's account is inactive
 * @param userHasNotSelectedTerminationBenefits - Whether the user hasn't made a termination benefits decision
 * @param userIsInCobraEnrollmentWindow - Whether the user is within the 60-day COBRA enrollment window
 * @param userIsCobraEligible - Whether the user is eligible for COBRA coverage
 * @param userHasSelectedCobra - Whether the user has opted into COBRA
 * @param userHasDeclinedTerminationBenefits - Whether the user has declined termination benefits
 * @returns JSX element for the banner or undefined if no banner should be shown
 */
export const makeCobraBanner = (
  userIsInactive?: boolean,
  userHasNotSelectedTerminationBenefits?: boolean,
  userIsInCobraEnrollmentWindow?: boolean,
  userIsCobraEligible?: boolean,
  userHasSelectedCobra?: boolean,
  userHasDeclinedTerminationBenefits?: boolean
): React.ReactElement | undefined => {
  let bannerTitle
  let bannerText
  let bannerType: ComponentProps<typeof Banner>['type'] = 'warning'
  let bannerCta

  if (userIsInactive) {
    let bannerCtaText

    if (userHasNotSelectedTerminationBenefits) {
      if (userIsCobraEligible && userIsInCobraEnrollmentWindow) {
        bannerTitle = 'Want to keep your Thatch balance?'
        bannerText = <>You own your actual insurance coverage, so those plans are yours to keep or cancel as you wish. But if you want to keep using the Thatch balance you've already saved, you can by <TextLink href='https://support.thatch.com/leaving-thatch-as-an-employee'>opting into COBRA</TextLink></>
        bannerCtaText = 'How does COBRA work?'
      } else {
        bannerTitle = 'What\'s next?'
        bannerText = <>Your employer will no longer be covering your healthcare costs, but the actual insurance coverage is yours to keep or cancel.</>
        bannerCtaText = 'Learn more →'
      }
    }

    if (userHasSelectedCobra) {
      bannerTitle = '🐍 COBRA is active 🐍'
      bannerText = <>You're getting your monthly allowance and access to your Thatch balance as long as it continues.</>
      bannerType = 'caution'
      bannerCtaText = 'How does COBRA work?'
    }

    if (userHasDeclinedTerminationBenefits) {
      bannerTitle = 'You declined COBRA'
      bannerText = <>You no longer have access to your Thatch balance. You own your actual insurance coverage, so those plans are yours to keep or cancel as you wish.</>
      bannerType = 'critical'
      bannerCtaText = 'Learn more →'
    }

    bannerCta = <ButtonLink href={membersContinuationOfCoverage.index()}>{bannerCtaText}</ButtonLink>
  }

  if (bannerText) {
    return (
      <div className='mb-6'>
        <Banner
          cta={bannerCta}
          title={bannerTitle}
          type={bannerType}
        >
          <p className='mb-4'>{bannerText}</p>
        </Banner>
      </div>
    )
  }
}

const checkEnrollmentsForHeaderElements = (
  enrollments: MembersEnrollment[],
  today: Date,
  canChangePlans: boolean
): { showFailedEnrollmentBanner: boolean; canChangePlans: boolean } => {
  let showFailedEnrollmentBanner = false

  for (const enrollment of enrollments) {
    if (showFailedEnrollmentBanner && canChangePlans) {
      // Since everything is true, no need to check the rest of the enrollments
      break
    }

    const progressState = enrollment.overview?.progress_state
    showFailedEnrollmentBanner = showFailedEnrollmentBanner || progressState === 'failed'

    if (!canChangePlans) {
      const startDate = enrollment.coverage_dates?.coverage_start_date ? new Date(enrollment.coverage_dates?.coverage_start_date) : null
      canChangePlans = enrollment.self_serve_change_dependents_enabled && (startDate !== null && startDate <= today)
    }
  }

  return {
    showFailedEnrollmentBanner,
    canChangePlans
  }
}

/**
 * Determines which header elements should be displayed based on enrollment data and plan year.
 *
 * @param enrollments - Categorized enrollments data containing medical, dental, and vision enrollments
 * @param selectedPlanYear - The currently selected plan year
 * @param activeQLESubtype - The active QLE subtype, if it exists. If it exists, the member should not see "change plans".
 * @returns Object containing failedEnrollmentTypes, and canChangePlans booleans
 */
export const determineHeaderBooleans = (
  enrollments: MembersCategorizedEnrollments | undefined,
  selectedPlanYear: number,
  activeQLESubtype: null | string = null
): { failedEnrollmentTypes: Set<string>; canChangePlans: boolean } => {
  const failedEnrollmentTypes = new Set<string>()
  let canChangePlans = false
  const today = new Date()

  if (selectedPlanYear === today.getFullYear()) {
    const medicalEnrollmentCheck = checkEnrollmentsForHeaderElements(
      enrollments?.medical_enrollments || [],
      today,
      canChangePlans
    )

    if (medicalEnrollmentCheck.showFailedEnrollmentBanner) {
      failedEnrollmentTypes.add('medical')
    }
    canChangePlans = medicalEnrollmentCheck.canChangePlans

    const dentalEnrollmentCheck = checkEnrollmentsForHeaderElements(
      enrollments?.dental_enrollments || [],
      today,
      canChangePlans
    )

    if (dentalEnrollmentCheck.showFailedEnrollmentBanner) {
      failedEnrollmentTypes.add('dental')
    }
    canChangePlans = canChangePlans || dentalEnrollmentCheck.canChangePlans

    const visionEnrollmentCheck = checkEnrollmentsForHeaderElements(
      enrollments?.vision_enrollments || [],
      today,
      canChangePlans
    )

    if (visionEnrollmentCheck.showFailedEnrollmentBanner) {
      failedEnrollmentTypes.add('vision')
    }
    canChangePlans = canChangePlans || visionEnrollmentCheck.canChangePlans
  }

  // The user cannot change plans if there's an active QLE subtype,
  // they have to resume the QLE flow.
  // This has to be set at the end to make sure it is not overridden
  if (activeQLESubtype) {
    canChangePlans = false
  }

  return {
    failedEnrollmentTypes,
    canChangePlans
  }
}

/**
 * Determines whether the instructions section should be displayed.
 *
 * Shows the section when:
 * 1. renewalsV2Enabled is true, AND
 * 2. There are instructions present, AND
 * 3. Either:
 *    a. There are incomplete instructions (status !== 'completed'), OR
 *    b. NOT all enrollments are both completed AND coverage has started
 *       (i.e. show completed instructions until all enrollments are completed and coverage has started)
 *
 * @param renewalsV2Enabled - Whether the renewals v2 feature is enabled
 * @param instructions - Array of instruction responses
 * @param enrollments - Categorized enrollments data containing medical, dental, and vision enrollments
 * @returns Boolean indicating whether to show the instructions section
 */
export const shouldShowInstructionsSection = (
  renewalsV2Enabled: boolean,
  instructions: MembersInstruction[] | undefined,
  enrollments: MembersCategorizedEnrollments | undefined
): boolean => {
  if (!renewalsV2Enabled || !instructions || instructions.length === 0) {
    return false
  }

  const hasIncompleteInstruction = instructions.some((instruction) => instruction.status === 'pending')
  if (hasIncompleteInstruction) {
    return true
  }
  const allEnrollments: MembersEnrollment[] = [
    ...(enrollments?.medical_enrollments || []),
    ...(enrollments?.dental_enrollments || []),
    ...(enrollments?.vision_enrollments || [])
  ]

  const allEnrollmentsCompletedAndStarted = allEnrollments.every((enrollment) => {
    const enrollmentCompleted = enrollment?.overview?.progress_state === 'enrolled'
    const coverageStarted = enrollment?.coverage_dates?.coverage_start_date && new Date(enrollment.coverage_dates.coverage_start_date) <= new Date()
    return enrollmentCompleted && coverageStarted
  })

  // If any not happy path, show the instructions section
  return !allEnrollmentsCompletedAndStarted
}

/**
 * Creates plan cards UI components and calculates layout properties.
 *
 * @param enrollments - Categorized enrollments data containing medical, dental, and vision enrollments
 * @param enrollmentsError - Whether there was an error loading enrollments
 * @param isPriorYear - Whether the selected plan year is prior to the current year
 * @returns Object containing planCards JSX, numberOfCoverageTypes, and layout flags
 */
export const makePlanCards = (
  enrollments: MembersCategorizedEnrollments | undefined,
  enrollmentsError: boolean,
  isPriorYear: boolean
): { planCards: React.ReactElement; numberOfCoverageTypes: number; nextToEnrollmentCardWithFinancialStatus: boolean } => {
  if (enrollmentsError) {
    const planCards = (
      <div className='flex flex-col gap-2 min-w-[18rem] w-full'>
        <div className={`border border-gray-200 rounded-2xl p-3 shadow-sm ${isPriorYear ? 'bg-gray-50' : 'bg-white'} flex flex-col`}>
          <p className='text-center'>We're having trouble loading your plans</p>
        </div>
      </div>
    )
    return {
      planCards,
      numberOfCoverageTypes: 1,
      nextToEnrollmentCardWithFinancialStatus: true
    }
  }

  const anyMedicalPlan = (enrollments?.medical_enrollments?.length ?? 0) > 0
  const anyDentalPlan = (enrollments?.dental_enrollments?.length ?? 0) > 0
  const anyVisionPlan = (enrollments?.vision_enrollments?.length ?? 0) > 0

  const medicalEnrollments = sortEnrollments(enrollments?.medical_enrollments || [], isPriorYear)
  const dentalEnrollments = sortEnrollments(enrollments?.dental_enrollments || [], isPriorYear)
  const visionEnrollments = sortEnrollments(enrollments?.vision_enrollments || [], isPriorYear)

  const showCounter = medicalEnrollments.length > 1 || dentalEnrollments.length > 1 || visionEnrollments.length > 1

  const planCards = (
    <>
      {anyMedicalPlan && <EnrollmentCard allEnrollments={medicalEnrollments} isPriorYear={isPriorYear} showCounter={showCounter} />}
      {anyDentalPlan && <EnrollmentCard allEnrollments={dentalEnrollments} isPriorYear={isPriorYear} showCounter={showCounter} />}
      {anyVisionPlan && <EnrollmentCard allEnrollments={visionEnrollments} isPriorYear={isPriorYear} showCounter={showCounter} />}
    </>
  )

  const numberOfCoverageTypes = (anyMedicalPlan ? 1 : 0) + (anyDentalPlan ? 1 : 0) + (anyVisionPlan ? 1 : 0)

  const nextToEnrollmentCardWithFinancialStatus = Boolean(numberOfCoverageTypes === 1 && (
    (anyMedicalPlan && enrollmentDisplayHelper(medicalEnrollments[0], isPriorYear).showFinancialStatus) ||
    (anyDentalPlan && enrollmentDisplayHelper(dentalEnrollments[0], isPriorYear).showFinancialStatus) ||
    (anyVisionPlan && enrollmentDisplayHelper(visionEnrollments[0], isPriorYear).showFinancialStatus)
  ))

  return {
    planCards,
    numberOfCoverageTypes,
    nextToEnrollmentCardWithFinancialStatus
  }
}

export const MemberHomePage: React.FC<MemberHomePageProps> = ({
  changePlanPath,
  memberGivenName,
  memberOnboardingDate,
  enrollmentWindowEndDate,
  planYears,
  qmeEnabled,
  renewalsV2Enabled,
  showRenewalsBanner,
  showOnboardingBanner,
  activeQLESubtype,
  qlesV2Enabled,
  userIsInactive,
  userHasNotSelectedTerminationBenefits,
  userIsInCobraEnrollmentWindow,
  userIsCobraEligible,
  userHasSelectedCobra,
  userHasDeclinedTerminationBenefits,
  hasConfirmedMedicareEnrollment,
  changePlansDisabledAfterSpecialEnrollmentCoverageStartDate
}) => {
  const { breakpointIsAtLeast } = useBreakpoint()
  // Calculate the latest plan year
  const latestPlanYear = useMemo(() => Math.max(...planYears), [planYears])

  // Initial plan year shown on homepage
  const initialPlanYear = useMemo(() => {
    const currentYear = new Date().getFullYear()
    // Use the current year if we have plans for it
    if (planYears.includes(currentYear)) {
      return currentYear
    }
    // Otherwise use latest plan year
    if (planYears.length > 0) {
      return latestPlanYear
    }
    return 0
  }, [planYears, latestPlanYear])
  const [selectedPlanYear, setSelectedPlanYear] = useState(initialPlanYear)

  // Get user ID from window.segmentUserId (set by Rails)
  const userId = typeof window !== 'undefined' ? (window as any).segmentUserId : undefined

  if (planYears.length === 0) {
    return (
      <div className='flex flex-col w-full gap-6'>
        <UnrecoverableErrorBanner />
      </div>
    )
  }

  const handlePlanYearChange = (year: number) => {
    window.segment?.track('[EE] Plan year changed', {
      plan_year: year,
      location: 'member_homepage',
    })
    setSelectedPlanYear(year)
  }

  // Determine if the selected plan year is prior to the current year
  const isPriorYear = useMemo(() => selectedPlanYear < new Date().getFullYear(), [selectedPlanYear])

  // **** BEGIN API CALLS ****

  const { data: memberBalance, isLoading: balanceAndBudgetLoading } = useGetMembersTransactionsBalanceAndBreakdowns(
    {
      plan_year: selectedPlanYear,
    }, {
      query: {
        refetchOnWindowFocus: false
      }
    }
  )

  const { data: enrollments, isLoading: enrollmentsLoading, isError: enrollmentsError } = useGetMembersEnrollments({
    plan_year: selectedPlanYear
  }, {
    query: {
      refetchOnWindowFocus: false
    }
  })

  // Fetch enrollments for the latest plan year separately to populate latestPlanYearEnrollments
  // This ensures we always have enrollments from the latest year, even if initialPlanYear is not the latest
  const { data: latestYearEnrollmentsData } = useGetMembersEnrollments({
    plan_year: latestPlanYear
  }, {
    query: {
      refetchOnWindowFocus: false,
      enabled: latestPlanYear !== selectedPlanYear // Only fetch if different from selectedPlanYear to avoid duplicate calls
    }
  })

  const { data: instructions, isLoading: instructionsLoading, isError: _instructionsError } = useGetMembersInstructions({
    responsibility: 'member'
  }, {
    query: {
      refetchOnWindowFocus: false,
      enabled: renewalsV2Enabled
    }
  })

  // Capture enrollments from the latest plan year and never update
  // This reference is stable and only gets set once when data is available from the latest plan year
  // Even if selectedPlanYear changes and enrollments refetches, this ref will not update
  const latestPlanYearEnrollments = useRef<MembersCategorizedEnrollments | undefined>(undefined)
  if (!latestPlanYearEnrollments.current) {
    // Use enrollments from selectedPlanYear if it's the latest year, otherwise use the separate latestYearEnrollmentsData
    const latestPlanYearEnrollmentsData = selectedPlanYear === latestPlanYear ? enrollments : latestYearEnrollmentsData
    if (latestPlanYearEnrollmentsData) {
      latestPlanYearEnrollments.current = latestPlanYearEnrollmentsData
    }
  }

  const showInstructionsSection = useMemo(() => {
    return shouldShowInstructionsSection(renewalsV2Enabled ?? false, instructions?.data, latestPlanYearEnrollments.current)
  }, [renewalsV2Enabled, instructions, latestPlanYearEnrollments.current])

  const { data: transactions, isLoading: transactionsLoading, isError: transactionsError } = useGetMembersTransactions({
    plan_year: selectedPlanYear,
    'page[number]': 1,
    'page[size]': 3,
  }, {
    query: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  })

  // We don't care about the error state, we will just gracefully show the card without a highlighted product
  const { data: marketplaceProducts, isLoading: marketplaceLoading } = useGetMarketplaceProducts(undefined, {
    query: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  })

  // **** END API CALLS ****

  // Greeting changes based on the time of day & user's onboarding date
  const greeting = useMemo(() => getGreeting(memberGivenName, memberOnboardingDate), [memberGivenName, memberOnboardingDate])

  // Determine if we can show the buttons for changing plans
  const { failedEnrollmentsBannerTitle, canChangePlans } = useMemo(() => {
    const { failedEnrollmentTypes, canChangePlans } = determineHeaderBooleans(enrollments, selectedPlanYear, activeQLESubtype)
    let failedEnrollmentsBannerTitle

    if (failedEnrollmentTypes.size > 0) {
      failedEnrollmentsBannerTitle = `Your ${listToProseString(Array.from(failedEnrollmentTypes))} enrollment${failedEnrollmentTypes.size > 1 ? 's' : ''} didn't go through as expected`
    }

    return {
      failedEnrollmentsBannerTitle,
      canChangePlans
    }
  }, [enrollments, selectedPlanYear, activeQLESubtype])

  // Balance and budget header - Do not show if API is loading
  const balanceAndBudgetHeader = useMemo(() => {
    if (balanceAndBudgetLoading) {
      return null
    }

    return (
      <BalanceAndBudgetHeader planYear={selectedPlanYear} memberBalance={memberBalance} />
    )
  }, [balanceAndBudgetLoading, selectedPlanYear, memberBalance])

  // The header above the plan cards can only show the "Change plans" button
  // if the user is allowed to change plans for one of their enrollments.

  // NOTE: The button is disabled until 1/1/2026 PST due to INC-185.
  const isChangePlansDisabledByDate = useMemo(() => {
    // 1/1/2026 00:00:00 PST is 1/1/2026 08:00:00 UTC
    const enableDate = new Date('2026-01-01T08:00:00Z')
    return new Date() < enableDate
  }, [])

  const isChangePlansDisabled = isChangePlansDisabledByDate ||
    (hasConfirmedMedicareEnrollment ?? false) ||
    Boolean(changePlansDisabledAfterSpecialEnrollmentCoverageStartDate)

  const medicareBlockTooltip = "You can't edit your Medicare coverage or change plans. If something looks wrong, reach out to support@thatch.com."
  const specialEnrollmentBlockTooltip = changePlansDisabledAfterSpecialEnrollmentCoverageStartDate
    ? `You have a plan change for ${changePlansDisabledAfterSpecialEnrollmentCoverageStartDate} already in progress. Reach out to support@thatch.com if you need to make changes.`
    : 'You have a plan change already in progress. Reach out to support@thatch.com if you need to make changes.'

  const planHeaderWithButton = useMemo(() => {
    return (
      <div className='w-full relative mb-4'>
        <h2 className='heading-sm'>
          Your plans
        </h2>
        {canChangePlans && (
          <div className='absolute -top-2 right-0'>
            {!qlesV2Enabled
              ? (
                <Tooltip
                  content='Reach out to support@thatch.com if you need to add someone to your 2026 plan.'
                  enabled={isChangePlansDisabledByDate}
                >
                  {/* Wrap in div to capture hover events when button is disabled */}
                  <div className={isChangePlansDisabled ? 'cursor-not-allowed' : ''}>
                    <ButtonLink
                      href={changePlanPath}
                      variant='tertiary'
                      size='large'
                      disabled={isChangePlansDisabled}
                    >
                      Change plans
                    </ButtonLink>
                  </div>
                </Tooltip>
                )
              : (
                <Tooltip
                  content={
                    changePlansDisabledAfterSpecialEnrollmentCoverageStartDate
                      ? specialEnrollmentBlockTooltip
                      : medicareBlockTooltip
                  }
                  enabled={
                    (hasConfirmedMedicareEnrollment ?? false) ||
                    Boolean(changePlansDisabledAfterSpecialEnrollmentCoverageStartDate)
                  }
                >
                  <div className={isChangePlansDisabled ? 'cursor-not-allowed' : ''}>
                    <ButtonLink
                      href={changePlanPath}
                      variant='tertiary'
                      size='large'
                      disabled={isChangePlansDisabled}
                      onClick={() => {
                        window.segment?.track('[EE] QLE flow initiated', {
                          source: 'member_homepage',
                        })
                      }}
                    >
                      Change dependents or plans
                    </ButtonLink>
                  </div>
                </Tooltip>
                )}
          </div>
        )}
      </div>
    )
  }, [canChangePlans, changePlanPath, isChangePlansDisabled, isChangePlansDisabledByDate, hasConfirmedMedicareEnrollment, changePlansDisabledAfterSpecialEnrollmentCoverageStartDate, medicareBlockTooltip, specialEnrollmentBlockTooltip, qlesV2Enabled])

  // Build the plan cards & determine properties that impact the layout
  const { planCards, numberOfCoverageTypes, nextToEnrollmentCardWithFinancialStatus } = useMemo(() => {
    return makePlanCards(enrollments, enrollmentsError, isPriorYear)
  }, [enrollments, enrollmentsError, isPriorYear])

  // Explore Marketplace Section - Do not show if API is loading
  const exploreMarketplaceSection = useMemo(() => {
    if (marketplaceLoading) {
      return null
    }

    // We do not show the loading skeleton state on the homepage,
    // so we do not pass in the isLoading prop
    return (
      <section className='flex flex-col items-start self-stretch gap-4 h-full'>
        <h2 className='heading-sm'>
          Marketplace
        </h2>
        <ExploreMarketplaceCard
          nextToEnrollmentCardWithFinancialStatus={nextToEnrollmentCardWithFinancialStatus}
          filterToCategories={['Fitness', 'Preventative care', 'Sleep', 'Mental health', 'Telehealth']}
          data={marketplaceProducts?.data}
        />
      </section>
    )
  }, [marketplaceLoading, marketplaceProducts, nextToEnrollmentCardWithFinancialStatus])

  // Recent Transactions Section - Do not show if API is loading
  const recentTransactionsSection = useMemo(() => {
    if (transactionsLoading) {
      return null
    }

    // We do not show the loading skeleton state on the homepage,
    // so we do not pass in the isLoading prop
    return (
      <section className='flex flex-col h-full items-start self-stretch gap-4'>
        <h2 className='heading-sm'>
          Recent transactions
        </h2>
        <RecentTransactions
          planYear={selectedPlanYear}
          isPriorYear={isPriorYear}
          nextToAnotherCard={(numberOfCoverageTypes === 1 && !qmeEnabled) || (numberOfCoverageTypes > 1 && qmeEnabled)}
          data={transactions?.data}
          isError={transactionsError}
        />
      </section>
    )
  }, [transactionsLoading, transactionsError, transactions, selectedPlanYear, isPriorYear, numberOfCoverageTypes, qmeEnabled])

  const anythingIsLoading = balanceAndBudgetLoading ||
    enrollmentsLoading ||
    (qmeEnabled && marketplaceLoading) ||
    transactionsLoading ||
    instructionsLoading

  useHomePageTracking({
    userId,
    selectedPlanYear,
    planYears,
    enrollments,
    showRenewalsBanner,
    showOnboardingBanner,
    canChangePlans,
    qmeEnabled,
    userIsInactive,
    isLoading: anythingIsLoading,
  })

  if (anythingIsLoading) {
    // Return the full-page abstract skeleton if we're loading anything
    return (
      <>
        <div className='w-full pb-6'>
          <div className='flex justify-between gap-4'>
            <h1 className='heading-lg'>
              {greeting}
            </h1>
            <div className='w-[20%]'>
              <SkeletonItem heightClass='h-20' widthClass='w-full' />
            </div>
          </div>
        </div>
        <div className='w-full flex-1 flex flex-col items-center justify-center gap-6'>
          <div className='w-full'>
            <SkeletonItem heightClass='h-32' widthClass='w-[100%]' />
          </div>
          <div className='w-full'>
            <SkeletonItem heightClass='h-64' widthClass='w-[100%]' />
          </div>
          <div className='w-full'>
            <SkeletonItem heightClass='h-64' widthClass='w-[100%]' />
          </div>
        </div>
      </>
    )
  }

  return (
    <div className='w-full h-full flex flex-col'>

      {failedEnrollmentsBannerTitle && (
        <div className='mb-6'>
          <Banner
            title={failedEnrollmentsBannerTitle}
            type='critical'
          >
            <p>We're looking into why, but you can reach out to <TextLink href='mailto:support@thatch.com'>support@thatch.com</TextLink> if you'd like to check in on our progress</p>
          </Banner>
        </div>
      )}

      {showRenewalsBanner && <div className='mb-6'><MemberRenewalsBanner enrollmentWindowEndDate={enrollmentWindowEndDate} /></div>}

      {showOnboardingBanner && <div className='mb-6'><MemberOnboardingBanner /></div>}

      {activeQLESubtype && <div className='mb-6'><MemberQLEBanner qleSubtype={activeQLESubtype} /></div>}

      {makeCobraBanner(userIsInactive, userHasNotSelectedTerminationBenefits, userIsInCobraEnrollmentWindow, userIsCobraEligible, userHasSelectedCobra, userHasDeclinedTerminationBenefits)}

      <div className='flex flex-row items-start justify-between flex-wrap gap-3 mb-6'>
        <h1 className='heading-lg'>
          {greeting}
        </h1>
        {/*
          * The fixed height here is a trick to prevent the buttons from
          * increasing the height of the header's content box. This prevents
          * the page heading from jumping when navigating to and from pages
          * that don't have buttons in their header.
          */}
        <div className='flex flex-row items-center gap-3 xs:h-8'>
          <PlanYearPicker
            planYears={planYears}
            selectedPlanYear={selectedPlanYear}
            onPlanYearChange={handlePlanYearChange}
          />
        </div>
      </div>

      {/* Instructions Section */}
      {showInstructionsSection && !instructionsLoading && latestPlanYearEnrollments.current
        ? (
          <div className='mb-6'>
            <InstructionsSection instructions={instructions?.data ?? []} metadata={instructions?.metadata} />
          </div>
          )
        : null}

      {balanceAndBudgetHeader}

      {horizontalSeparator}

      {
          plansTransactionsAndMarketplace(
            breakpointIsAtLeast('lg'),
            numberOfCoverageTypes,
            planHeaderWithButton,
            planCards,
            exploreMarketplaceSection,
            recentTransactionsSection,
            qmeEnabled
          )
        }
    </div>
  )
}

export default MemberHomePage
